/**
 * ProcessingContext
 * Manages global processing state for cryptographic operations
 *
 * This context eliminates prop drilling for isProcessing and processingStage
 * which are used by: App, UploadTab, BlackHole, VerifyTab
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode
} from 'react';
import type { ProcessingStage } from '../types';

// ============================================================================
// Context Types
// ============================================================================

export interface ProcessingContextValue {
  /** Whether a processing operation is currently active */
  isProcessing: boolean;
  /** Current stage of the processing operation */
  processingStage: ProcessingStage;
  /** Set the processing state */
  setIsProcessing: (value: boolean) => void;
  /** Set the current processing stage */
  setProcessingStage: (stage: ProcessingStage) => void;
  /** Reset processing to initial idle state */
  resetProcessing: () => void;
}

// ============================================================================
// Context Creation
// ============================================================================

const ProcessingContext = createContext<ProcessingContextValue | null>(null);

ProcessingContext.displayName = 'ProcessingContext';

// ============================================================================
// Provider Component
// ============================================================================

export interface ProcessingProviderProps {
  children: ReactNode;
  /** Optional initial processing state for testing */
  initialIsProcessing?: boolean;
  /** Optional initial processing stage for testing */
  initialProcessingStage?: ProcessingStage;
}

/**
 * ProcessingProvider
 * Wraps the application to provide processing state to all components
 */
export function ProcessingProvider({
  children,
  initialIsProcessing = false,
  initialProcessingStage = 'idle'
}: ProcessingProviderProps): JSX.Element {
  const [isProcessing, setIsProcessingState] = useState<boolean>(initialIsProcessing);
  const [processingStage, setProcessingStageState] = useState<ProcessingStage>(initialProcessingStage);

  // Stable callback references using useCallback
  const setIsProcessing = useCallback((value: boolean): void => {
    setIsProcessingState(value);
  }, []);

  const setProcessingStage = useCallback((stage: ProcessingStage): void => {
    setProcessingStageState(stage);
  }, []);

  const resetProcessing = useCallback((): void => {
    setIsProcessingState(false);
    setProcessingStageState('idle');
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<ProcessingContextValue>(
    () => ({
      isProcessing,
      processingStage,
      setIsProcessing,
      setProcessingStage,
      resetProcessing
    }),
    [isProcessing, processingStage, setIsProcessing, setProcessingStage, resetProcessing]
  );

  return (
    <ProcessingContext.Provider value={contextValue}>
      {children}
    </ProcessingContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

/**
 * useProcessing hook
 * Accesses the processing context from any component within the provider
 *
 * @throws Error if used outside of ProcessingProvider
 */
export function useProcessing(): ProcessingContextValue {
  const context = useContext(ProcessingContext);

  if (context === null) {
    throw new Error('useProcessing must be used within a ProcessingProvider');
  }

  return context;
}

// ============================================================================
// Exports
// ============================================================================

export { ProcessingContext };
